import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useAuth, useDictionary, usePage } from '@/src/app/hooks';
import { Container } from '@/src/ui';
import { type TextbookImageDTO } from '../lessons/DTOs';
import './LessonsPage.css';

import gTKCover from '@/src/assets/img/lessons/cover-grade-TK.jpg';
import gKCover from '@/src/assets/img/lessons/cover-grade-K.jpg';
import g1Cover from '@/src/assets/img/lessons/cover-grade-1.jpg';
import g2Cover from '@/src/assets/img/lessons/cover-grade-2.jpg';
import g3Cover from '@/src/assets/img/lessons/cover-grade-3.jpg';
import g4Cover from '@/src/assets/img/lessons/cover-grade-4.jpg';
import g5Cover from '@/src/assets/img/lessons/cover-grade-5.jpg';
import g6Cover from '@/src/assets/img/lessons/cover-grade-6.jpg';
import g6MCover from '@/src/assets/img/lessons/cover-grade-6M.jpg';
import g7Cover from '@/src/assets/img/lessons/cover-grade-7.jpg';
import g8Cover from '@/src/assets/img/lessons/cover-grade-8.jpg';

const LessonsPage: React.FC = () => {
    const { setSubheaderTitle, setSubheaderType } = usePage();
    const { user } = useAuth();
    const { translate } = useDictionary();
    document.title = translate('Lessons', 'Lessons');

    useEffect(() => {
        setSubheaderTitle(translate('Lessons'));
        setSubheaderType('default');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate]);

    const assignedBooks = useMemo(() => {
        if (!user?.allowedGrades) return [];

        const elementaryGrades = user.allowedGrades.elementary || [];
        const middleSchoolGrades = user.allowedGrades.middleSchool || [];

        return [...elementaryGrades, ...middleSchoolGrades].map((g) => {
            const gradeObj: TextbookImageDTO = {
                image: `/Content/assets/img/lessons/cover-grade-${g}.jpg`,
                code: g,
                viewAllLessonsLink: `/allLessons/${g}`,
                name: translate('Grade') + ' ' + g,
                teLink: `https://ebook.paradigmmath.com/grade${g}/teacher-edition`,
                seLink: `https://ebook.paradigmmath.com/grade${g}/teacher-edition`,
            };

            return gradeObj;
        });
    }, [user?.allowedGrades, translate]);

    return (
        <Container>
            <div className="lesson-grades-container">
                <div id="lesson-grades-container">
                    {assignedBooks.map((grade) => (
                        <div key={grade.code} className="grade-card">
                            <h2 className="grade-names">{grade.name}</h2>

                            <Link to={grade.viewAllLessonsLink}>
                                <img src={getGradeCover(grade.code)} alt={grade.name} />
                            </Link>

                            <Link
                                to={grade.viewAllLessonsLink}
                                className={`view-lessons pd-G${grade.code}-primary background`}
                            >
                                {translate('View All Lessons')}
                            </Link>

                            {grade.code !== 'TK' && (
                                <div>
                                    <a
                                        className="links lesson-page-links"
                                        href={grade.teLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {translate('Teacher Edition')}
                                    </a>

                                    <a
                                        className="links lesson-page-links"
                                        href={grade.seLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {translate('Student Edition')}
                                    </a>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Container>
    );
};

export default LessonsPage;

function getGradeCover(grade: string): string {
    switch (grade) {
        case 'TK':
            return gTKCover;
        case 'K':
            return gKCover;
        case '1':
            return g1Cover;
        case '2':
            return g2Cover;
        case '3':
            return g3Cover;
        case '4':
            return g4Cover;
        case '5':
            return g5Cover;
        case '6':
            return g6Cover;
        case '6M':
            return g6MCover;
        case '7':
            return g7Cover;
        case '8':
            return g8Cover;
        default:
            return '';
    }
}
